import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Mail, 
  Phone, 
  Building2, 
  Calendar,
  ChevronDown,
  ChevronUp,
  Trash2,
  Search,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { getAdminInquiries, updateInquiryStatus, deleteInquiry } from '../../services/api';

const AdminInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedInquiry, setExpandedInquiry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const data = await getAdminInquiries();
      setInquiries(data);
    } catch (error) {
      toast.error('Failed to fetch inquiries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      await updateInquiryStatus(inquiryId, newStatus);
      setInquiries(prev => 
        prev.map(i => i.id === inquiryId ? { ...i, status: newStatus } : i)
      );
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    try {
      await deleteInquiry(inquiryId);
      setInquiries(prev => prev.filter(i => i.id !== inquiryId));
      toast.success('Inquiry deleted');
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-red-100 text-red-700 border-red-200';
      case 'read': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'responded': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const filteredInquiries = inquiries.filter(inquiry => {
    const matchesSearch = !searchQuery || 
      inquiry.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inquiry.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inquiry.subject.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || inquiry.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const statusCounts = {
    all: inquiries.length,
    new: inquiries.filter(i => i.status === 'new').length,
    read: inquiries.filter(i => i.status === 'read').length,
    responded: inquiries.filter(i => i.status === 'responded').length
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--primary-teal)] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-inquiries">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--primary-teal)' }}>
          Inquiries
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-medium)' }}>
          Manage customer inquiries and messages
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={18} style={{ color: 'var(--text-light)' }} />
          <Input
            placeholder="Search by name, email, or subject..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="inquiry-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'new', 'read', 'responded'].map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
              className={statusFilter === status ? 'btn-gold' : ''}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)} ({statusCounts[status]})
            </Button>
          ))}
        </div>
      </div>

      {/* Inquiries List */}
      <div className="space-y-4">
        {filteredInquiries.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="py-12 text-center">
              <MessageSquare size={48} className="mx-auto mb-4" style={{ color: 'var(--text-light)' }} />
              <p style={{ color: 'var(--text-medium)' }}>
                {searchQuery || statusFilter !== 'all' ? 'No inquiries match your filters' : 'No inquiries yet'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredInquiries.map((inquiry) => (
            <Card key={inquiry.id} className="border-0 shadow-md overflow-hidden">
              <div 
                className={`p-4 cursor-pointer hover:bg-gray-50 ${inquiry.status === 'new' ? 'border-l-4 border-l-red-500' : ''}`}
                onClick={() => {
                  setExpandedInquiry(expandedInquiry === inquiry.id ? null : inquiry.id);
                  if (inquiry.status === 'new') {
                    handleStatusChange(inquiry.id, 'read');
                  }
                }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold" style={{ color: 'var(--primary-teal)' }}>
                        {inquiry.name}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(inquiry.status)}`}>
                        {inquiry.status}
                      </span>
                    </div>
                    <p className="text-sm font-medium mt-1" style={{ color: 'var(--text-dark)' }}>
                      {inquiry.subject}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: 'var(--text-medium)' }}>
                      <span className="flex items-center gap-1">
                        <Mail size={12} /> {inquiry.email}
                      </span>
                      {inquiry.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} /> {inquiry.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs whitespace-nowrap" style={{ color: 'var(--text-light)' }}>
                      {new Date(inquiry.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short'
                      })}
                    </span>
                    {expandedInquiry === inquiry.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedInquiry === inquiry.id && (
                <div className="border-t bg-gray-50 p-4">
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail size={16} style={{ color: 'var(--primary-teal)' }} />
                      <a href={`mailto:${inquiry.email}`} className="hover:underline" style={{ color: 'var(--primary-teal)' }}>
                        {inquiry.email}
                      </a>
                    </div>
                    {inquiry.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone size={16} style={{ color: 'var(--primary-teal)' }} />
                        <a href={`tel:${inquiry.phone}`} className="hover:underline" style={{ color: 'var(--primary-teal)' }}>
                          {inquiry.phone}
                        </a>
                      </div>
                    )}
                    {inquiry.company && (
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 size={16} style={{ color: 'var(--text-medium)' }} />
                        <span>{inquiry.company}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={16} style={{ color: 'var(--text-medium)' }} />
                      <span>
                        {new Date(inquiry.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>

                  {inquiry.productInterest && (
                    <div className="mb-4">
                      <span className="text-xs font-medium" style={{ color: 'var(--text-medium)' }}>Product Interest:</span>
                      <span 
                        className="ml-2 px-2 py-1 rounded text-sm"
                        style={{ backgroundColor: 'var(--gold-accent)', color: 'white' }}
                      >
                        {inquiry.productInterest}
                      </span>
                    </div>
                  )}

                  <div className="bg-white rounded-lg p-4 mb-4 border">
                    <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-dark)' }}>Message:</p>
                    <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text-medium)' }}>
                      {inquiry.message}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div className="flex gap-2">
                      <select
                        value={inquiry.status}
                        onChange={(e) => handleStatusChange(inquiry.id, e.target.value)}
                        className="px-3 py-1.5 rounded border text-sm"
                        style={{ borderColor: 'var(--gold-accent)' }}
                      >
                        <option value="new">New</option>
                        <option value="read">Read</option>
                        <option value="responded">Responded</option>
                      </select>
                      <Button
                        size="sm"
                        onClick={() => window.open(`mailto:${inquiry.email}?subject=Re: ${inquiry.subject}`, '_blank')}
                        className="btn-gold"
                      >
                        Reply via Email
                      </Button>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(inquiry.id)}
                    >
                      <Trash2 size={16} className="mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminInquiries;
